import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Select from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import SupplyLandAuthenticatedReCaptcha from "@SupplyLand/Components/SupplyLandAuthenticatedReCaptcha";
import sendTaxCertificate from "@SupplyLand/Handlers/SendTaxCertificate";
import * as React from "react";
import { RefObject } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    billTo: getBillToState(state, state.context.session.billToId).value,
});
const mapDispatchToProps = {
    sendTaxCertificate,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: React.RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
};

export interface TaxExemptionFormFieldStyles {
    container?: GridContainerProps;
    firstNameGridItem?: GridItemProps;
    lastNameGridItem?: GridItemProps;
    address1GridItem?: GridItemProps;
    address2GridItem?: GridItemProps;
    cityGridItem?: GridItemProps;
    stateGridItem?: GridItemProps;
    postalCodeGridItem?: GridItemProps;
    phoneNumberGridItem?: GridItemProps;
    emailAddressGridItem?: GridItemProps;
    feinNumberGridItem?: GridItemProps;
    stateRegistrationNumberGridItem?: GridItemProps;
    fileGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    fileUploadGridContainer?: GridContainerProps;
    fileUploadGridItem?: GridItemProps;
    addUploadComponentGridItem?: GridItemProps;
    addUploadButtonStyle?: ButtonPresentationProps;
    removeFileUploadGridItem?: GridItemProps;
    fileUploadWrapperGridContainer?: GridContainerProps;
    fileUploadWrapperGridItem?: GridItemProps;
    removeUploadButtonStyle?: ButtonPresentationProps;
    firstRemoveUploadButtonStyle?: ButtonPresentationProps;
    submitButton: ButtonPresentationProps;
    recaptchaGridItem: GridItemProps;
}

export const taxExemptionFormFieldStyles: TaxExemptionFormFieldStyles = {
    container: {
        offsetCss: css`
            padding-bottom: 25px;
        `,
    },
    firstNameGridItem: { width: 6 },
    lastNameGridItem: { width: 6 },
    address1GridItem: { width: 6 },
    address2GridItem: { width: 6 },
    cityGridItem: { width: 6 },
    stateGridItem: { width: 6 },
    postalCodeGridItem: { width: 6 },
    phoneNumberGridItem: { width: 6 },
    emailAddressGridItem: { width: 12 },
    feinNumberGridItem: { width: 6 },
    stateRegistrationNumberGridItem: { width: 6 },
    buttonGridItem: { width: 12 },
    fileGridItem: { width: 12 },
    fileUploadWrapperGridContainer: {},
    fileUploadWrapperGridItem: { width: 12 },
    fileUploadGridContainer: {},
    fileUploadGridItem: {
        width: 9,
        css: css`
            > div {
                width: 100%;
            },
        `,
    },
    removeFileUploadGridItem: { width: 3 },
    addUploadComponentGridItem: { width: 12 },
    removeUploadButtonStyle: {
        css: css`
            color: black;
        `,
    },
    firstRemoveUploadButtonStyle: {
        css: css`
            margin-top: 32px;
            color: black;
        `,
    },
    addUploadButtonStyle: {
        css: css`
            color: black;
        `,
    },
    submitButton: {
        css: css`
            color: black;
        `,
    },
    recaptchaGridItem: {
        width: 12,
    },
};
const pdf = "";

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
const UploadTaxExemptionForm = ({ sendTaxCertificate, billTo, validateReCaptcha }: Props) => {
    const styles = taxExemptionFormFieldStyles;
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();

    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const firstNameRequiredFieldMessage = siteMessage("TaxExemptionForm_FirstName_Required");
    const [firstName, setFirstName] = React.useState("");
    const [firstNameError, setFirstNameError] = React.useState(firstNameRequiredFieldMessage);

    const lastNameRequiredFieldMessage = siteMessage("TaxExemptionForm_LastName_Required");
    const [lastName, setLastName] = React.useState("");
    const [lastNameError, setLastNameError] = React.useState(lastNameRequiredFieldMessage);

    const address1RequiredFieldMessage = siteMessage("TaxExemptionForm_Address1_Required");
    const [address1, setAddress1] = React.useState("");
    const [address1Error, setAddress1Error] = React.useState(address1RequiredFieldMessage);

    const [address2, setAddress2] = React.useState("");

    const cityRequiredFieldMessage = siteMessage("TaxExemptionForm_City_Required");
    const [city, setCity] = React.useState("");
    const [cityError, setCityError] = React.useState(cityRequiredFieldMessage);

    const stateRequiredFieldMessage = siteMessage("TaxExemptionForm_State_Required");
    const [state, setState] = React.useState("");
    const [stateError, setStateError] = React.useState(stateRequiredFieldMessage);

    const feinNumberRequiredFieldMessage = siteMessage("TaxExemptionForm_FeinNumber_Required");
    const [feinNumber, setFeinNumber] = React.useState("");
    const [feinNumberError, setFeinNumberError] = React.useState(feinNumberRequiredFieldMessage);

    const stateRegistrationNumberRequiredFieldMessage = siteMessage(
        "TaxExemptionForm_StateRegistrationNumber_Required",
    );
    const [stateRegistrationNumber, setStateRegistrationNumber] = React.useState("");
    const [stateRegistrationNumberError, setStateRegistrationNumberError] = React.useState(
        stateRegistrationNumberRequiredFieldMessage,
    );

    const postalCodeRequiredFieldMessage = siteMessage("TaxExemptionForm_PostalCode_Required");
    const [postalCode, setPostalCode] = React.useState("");
    const [postalCodeError, setPostalCodeError] = React.useState(postalCodeRequiredFieldMessage);

    const phoneNumberRequiredFieldMessage = siteMessage("ContactUsForm_PhoneNumberIsRequiredErrorMessage");
    const phoneNumberInvalidFieldMessage = siteMessage("ContactUsForm_PhoneNumberIsInvalidErrorMessage");
    const phoneRegex = /^([\(\)/\-\.\+\s]*\d\s?(ext)?[\(\)/\-\.\+\s]*){10,}$/;
    const [phoneNumber, setPhoneNumber] = React.useState("");
    const [phoneNumberError, setPhoneNumberError] = React.useState(phoneNumberRequiredFieldMessage);

    const emailRequiredFieldMessage = siteMessage("ContactUsForm_EmailIsRequiredErrorMessage");
    const emailInvalidFieldMessage = siteMessage("ContactUsForm_EmailIsInvalidErrorMessage");
    const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
    const [emailAddress, setEmailAddress] = React.useState("");
    const [emailAddressError, setEmailAddressError] = React.useState(emailRequiredFieldMessage);

    const pdfRequiredFieldMessage = siteMessage("TaxExemptionForm_Pdf_Required");
    const [pdfError, setPdfError] = React.useState(pdfRequiredFieldMessage);

    class UploadComponentModel {
        constructor(key: string, fileName?: string, file?: Record<string, any>, pdf?: string) {
            this.key = key;
            this.fileName = fileName ?? undefined;
            this.file = file ?? undefined;
            this.pdf = pdf ?? "";
        }
        getKey() {
            return this.key;
        }
        getFileName() {
            return this.fileName;
        }
        getFile() {
            return this.file;
        }
        getPdf() {
            return this.getPdf;
        }
        key: string;
        fileName?: string;
        file?: Record<string, any>;
        pdf: string;
    }

    const initialComponents = [new UploadComponentModel("uploadComponent_0")];
    const [uploadComponents, setUploadComponents] = React.useState<UploadComponentModel[]>(initialComponents);
    const [counter, setCounter] = React.useState(0);

    const toasterContext = React.useContext(ToasterContext);

    const stateAbbreviations = [
        <option key={0} hidden disabled value="">
            Select
        </option>,
        <option key={1} value="AL">
            Alabama
        </option>,
        <option key={2} value="AK">
            Alaska
        </option>,
        <option key={3} value="AZ">
            Arizona
        </option>,
        <option key={4} value="CA">
            California
        </option>,
        <option key={5} value="CO">
            Colorado
        </option>,
        <option key={6} value="CT">
            Connecticut
        </option>,
        <option key={7} value="DE">
            Deleware
        </option>,
        <option key={8} value="FL">
            Florida
        </option>,
        <option key={9} value="GA">
            Georgia
        </option>,
        <option key={10} value="HI">
            Hawaii
        </option>,
        <option key={11} value="ID">
            Idaho
        </option>,
        <option key={12} value="IL">
            Illinois
        </option>,
        <option key={13} value="IN">
            Indiana
        </option>,
        <option key={14} value="IA">
            Iowa
        </option>,
        <option key={15} value="KS">
            Kansas
        </option>,
        <option key={16} value="KY">
            Kentucky
        </option>,
        <option key={17} value="LA">
            Louisiana
        </option>,
        <option key={18} value="ME">
            Maine
        </option>,
        <option key={19} value="MD">
            Maryland
        </option>,
        <option key={20} value="MA">
            Massachusetts
        </option>,
        <option key={21} value="MI">
            Michigan
        </option>,
        <option key={22} value="MN">
            Minnesota
        </option>,
        <option key={23} value="MS">
            Mississippi
        </option>,
        <option key={24} value="MO">
            Missouri
        </option>,
        <option key={25} value="MT">
            Montana
        </option>,
        <option key={26} value="NE">
            Nebraska
        </option>,
        <option key={27} value="NV">
            Nevada
        </option>,
        <option key={28} value="NH">
            New Hampshire
        </option>,
        <option key={29} value="NJ">
            New Jersey
        </option>,
        <option key={30} value="NM">
            New Mexico
        </option>,
        <option key={31} value="NY">
            New York
        </option>,
        <option key={32} value="NC">
            North Carolina
        </option>,
        <option key={33} value="ND">
            North Dakota
        </option>,
        <option key={34} value="OH">
            Ohio
        </option>,
        <option key={35} value="OK">
            Oklahoma
        </option>,
        <option key={36} value="OR">
            Oregon
        </option>,
        <option key={37} value="PA">
            Pennsylvania
        </option>,
        <option key={38} value="RI">
            Rhode Island
        </option>,
        <option key={39} value="SC">
            South Carolina
        </option>,
        <option key={40} value="SD">
            South Dakota
        </option>,
        <option key={41} value="TN">
            Tennessee
        </option>,
        <option key={42} value="TX">
            Texas
        </option>,
        <option key={43} value="UT">
            Utah
        </option>,
        <option key={44} value="VT">
            Vermont
        </option>,
        <option key={45} value="VA">
            Virginia
        </option>,
        <option key={46} value="WA">
            Washington
        </option>,
        <option key={47} value="WV">
            West Virginia
        </option>,
        <option key={48} value="WI">
            Wisconsin
        </option>,
        <option key={49} value="WY">
            Wyoming
        </option>,
    ];

    // First Name
    const firstNameChangeHandler = (name: string) => {
        validateFirstName(name);
        setFirstName(name);
    };
    const validateFirstName = (name: string) => {
        const errorMessage = !name ? firstNameRequiredFieldMessage : "";
        setFirstNameError(errorMessage);
        return !errorMessage;
    };

    // Last Name
    const lastNameChangeHandler = (name: string) => {
        validateLastName(name);
        setLastName(name);
    };
    const validateLastName = (name: string) => {
        const errorMessage = !name ? lastNameRequiredFieldMessage : "";
        setLastNameError(errorMessage);
        return !errorMessage;
    };

    // Address 1
    const address1ChangeHandler = (name: string) => {
        validateAddress1(name);
        setAddress1(name);
    };
    const validateAddress1 = (name: string) => {
        const errorMessage = !name ? address1RequiredFieldMessage : "";
        setAddress1Error(errorMessage);
        return !errorMessage;
    };

    // Address 2
    const address2ChangeHandler = (name: string) => {
        setAddress2(name);
    };

    // City
    const cityChangeHandler = (name: string) => {
        validateCity(name);
        setCity(name);
    };
    const validateCity = (name: string) => {
        const errorMessage = !name ? cityRequiredFieldMessage : "";
        setCityError(errorMessage);
        return !errorMessage;
    };

    // State
    const stateChangeHandler = (name: string) => {
        validateState(name);
        setState(name);
    };
    const validateState = (name: string) => {
        const errorMessage = !name ? stateRequiredFieldMessage : "";
        setStateError(errorMessage);
        return !errorMessage;
    };

    // Postal Code
    const postalCodeChangeHandler = (name: string) => {
        validatePostalCode(name);
        setPostalCode(name);
    };
    const validatePostalCode = (name: string) => {
        const errorMessage = !name ? postalCodeRequiredFieldMessage : "";
        setPostalCodeError(errorMessage);
        return !errorMessage;
    };

    // Phone Number
    const formatPhoneNumber = (phoneNumberString: string) => {
        const formattedPhone = phoneNumberString.replace(/\D+/g, "").replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
        return formattedPhone;
    };
    const phoneNumberBlurHandler = (name: string) => {
        validatePhoneNumber(name);
    };
    const phoneNumberChangeHandler = (name: string) => {
        const formattedPhoneNumer = formatPhoneNumber(name);
        setPhoneNumber(formattedPhoneNumer);
    };
    const validatePhoneNumber = (name: string) => {
        const errorMessage = !name
            ? phoneNumberRequiredFieldMessage
            : !name || phoneRegex.test(name)
              ? ""
              : phoneNumberInvalidFieldMessage;
        setPhoneNumberError(errorMessage);
        return !errorMessage;
    };

    // Email Address
    const emailAddressChangeHandler = (name: string) => {
        validateEmailAddress(name);
        setEmailAddress(name);
    };
    const validateEmailAddress = (name: string) => {
        const errorMessage = !name
            ? emailRequiredFieldMessage
            : !name || emailRegexp.test(name)
              ? ""
              : emailInvalidFieldMessage;
        setEmailAddressError(errorMessage);
        return !errorMessage;
    };

    // FEIN Number
    const feinNumberChangeHandler = (name: string) => {
        validateFeinNumber(name);
        setFeinNumber(name);
    };
    const validateFeinNumber = (name: string) => {
        const errorMessage = !name ? feinNumberRequiredFieldMessage : "";
        setFeinNumberError(errorMessage);
        return !errorMessage;
    };

    // State Registration Number
    const stateRegistrationNumberChangeHandler = (name: string) => {
        validateStateRegistrationNumber(name);
        setStateRegistrationNumber(name);
    };
    const validateStateRegistrationNumber = (name: string) => {
        const errorMessage = !name ? feinNumberRequiredFieldMessage : "";
        setStateRegistrationNumberError(errorMessage);
        return !errorMessage;
    };

    const validatePdf = (name: string) => {
        const errorMessage = !name ? pdfRequiredFieldMessage : "";
        setPdfError(errorMessage);
        return !errorMessage;
    };

    const validatePdfSetToUpload = () => {
        let status = "noPdfSetToUpload";
        const currentUploadComponents = uploadComponents;
        for (let i = 0; i < currentUploadComponents.length; i++) {
            if (currentUploadComponents[i].pdf.length > 0) {
                status = "pdfSetToUpload";
            }
        }
        if (status === "noPdfSetToUpload") {
            setPdfError(pdfRequiredFieldMessage);
        } else {
            setPdfError("");
        }
        return status === "pdfSetToUpload";
    };

    const validateSubmitEnabled = () => {
        return !(
            address1Error ||
            stateError ||
            cityError ||
            postalCodeError ||
            lastNameError ||
            emailAddressError ||
            phoneNumberError ||
            feinNumberError ||
            stateRegistrationNumberError ||
            pdfError
        );
    };

    function getBase64(file: any) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    }

    const uploadTaxExemptionFormHandler = async () => {
        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        if (!validatePdfSetToUpload()) {
            return;
        }

        if (!validateSubmitEnabled()) {
            return;
        }

        if (recaptchaRef) {
            const isReCaptchaValid = await validateReCaptcha({ location: "ContactUs", recaptchaRef });
            if (!isReCaptchaValid) {
                return;
            }
        }

        setIsSubmitting(true);
        let status = "success";
        const currentUploadComponents = uploadComponents;
        const pdfStrings: string[] = [];
        for (let i = 0; i < currentUploadComponents.length; i++) {
            if (currentUploadComponents[i].fileName) {
                pdfStrings[i] = currentUploadComponents[i].pdf.split(",")[1];
            }
        }
        sendTaxCertificate({
            taxForm: {
                customerNumber: billTo?.customerNumber ?? "",
                pdfStrings,
                feinNumber,
                firstName,
                lastName,
                address1,
                address2,
                city,
                state,
                postalCode,
                phoneNumber,
                emailAddress,
                stateRegistrationNumber,
            },
            onComplete: response => {
                status = response.apiResult?.success ? "success" : "failure";
            },
        });
        status = "success";
        if (status === "success") {
            toasterContext.addToast({ body: siteMessage("TaxExemption_UploadSuccess"), messageType: "success" });
            resetForm();
        } else {
            toasterContext.addToast({ body: siteMessage("TaxExemption_UploadError"), messageType: "danger" });
        }
        setIsSubmitting(false);
    };

    const resetForm = () => {
        setFirstName("");
        setLastName("");
        setAddress1("");
        setAddress2("");
        setState("");
        setCity("");
        setPostalCode("");
        setPhoneNumber("");
        setEmailAddress("");
        setFeinNumber("");
        setStateRegistrationNumber("");
        setCounter(0);
        setUploadComponents([new UploadComponentModel("uploadComponent_0")]);
    };

    const handleFileUploadFileChangeEvent = (event: any, uploadComponentKey: string, file: any) => {
        const currentUploadComponents = uploadComponents;
        const targetUploadComponentIndex = currentUploadComponents.findIndex(x => x.key === uploadComponentKey);
        if (file) {
            validatePdf(file);
            currentUploadComponents[targetUploadComponentIndex].fileName = file.name;
            currentUploadComponents[targetUploadComponentIndex].file = file;
            getBase64(file).then(data => {
                currentUploadComponents[targetUploadComponentIndex].pdf = String(data);
            });
        } else {
            // if the user is deleting the file, remove it
            currentUploadComponents[targetUploadComponentIndex].fileName = undefined;
            currentUploadComponents[targetUploadComponentIndex].file = undefined;
            currentUploadComponents[targetUploadComponentIndex].pdf = "";
        }
        setUploadComponents(currentUploadComponents);
    };

    const addUploadComponent = () => {
        const incrementedCounter = counter + 1;
        const newComponent = new UploadComponentModel(`uploadComponent_${incrementedCounter}`);
        setUploadComponents(prevUploadComponents => [...prevUploadComponents, newComponent]);
        setCounter(incrementedCounter);
    };

    const removeUploadComponent = (uploadComponentKey: string) => {
        const currentUploadComponents = uploadComponents;
        const updatedUploadComponents = currentUploadComponents.filter(item => item.key !== uploadComponentKey);
        setUploadComponents(updatedUploadComponents);
    };

    const isSubmitEnabled = validateSubmitEnabled();

    return (
        <LoadingOverlay loading={isSubmitting} spinnerProps={{ color: "primary" }}>
            <GridContainer {...styles.container} data-test-selector="taxExemptionFormFields">
                <GridItem {...styles.firstNameGridItem}>
                    <TextField
                        name="firstName"
                        label={translate("First Name")}
                        value={firstName}
                        required
                        onChange={e => firstNameChangeHandler(e.currentTarget.value)}
                        error={isSubmitted && firstNameError}
                        data-test-selector="taxExemption_firstName"
                    />
                </GridItem>
                <GridItem {...styles.lastNameGridItem}>
                    <TextField
                        name="lastName"
                        label={translate("Last Name")}
                        value={lastName}
                        required
                        onChange={e => lastNameChangeHandler(e.currentTarget.value)}
                        error={isSubmitted && lastNameError}
                        data-test-selector="taxExemption_lastName"
                    />
                </GridItem>
                <GridItem {...styles.address1GridItem}>
                    <TextField
                        name="address1"
                        label={translate("Address 1")}
                        value={address1}
                        required
                        onChange={e => address1ChangeHandler(e.currentTarget.value)}
                        error={isSubmitted && address1Error}
                        data-test-selector="taxExemption_address1"
                    />
                </GridItem>
                <GridItem {...styles.address2GridItem}>
                    <TextField
                        name="address2"
                        label={translate("Address 2")}
                        value={address2}
                        onChange={e => address2ChangeHandler(e.currentTarget.value)}
                        data-test-selector="taxExemption_address2"
                    />
                </GridItem>
                <GridItem {...styles.cityGridItem}>
                    <TextField
                        name="city"
                        label={translate("City")}
                        value={city}
                        required
                        onChange={e => cityChangeHandler(e.currentTarget.value)}
                        error={isSubmitted && cityError}
                        data-test-selector="taxExemption_city"
                    />
                </GridItem>
                <GridItem {...styles.stateGridItem}>
                    <Select
                        label={translate("State")}
                        onChange={e => stateChangeHandler(e.currentTarget.value)}
                        value={state}
                        data-test-selector="createAccount_industry"
                        required
                        placeholder="Select"
                        error={isSubmitted && stateError}
                    >
                        {stateAbbreviations}
                    </Select>
                </GridItem>
                <GridItem {...styles.postalCodeGridItem}>
                    <TextField
                        name="postalCode"
                        label={translate("Postal Code")}
                        value={postalCode}
                        required
                        onChange={e => postalCodeChangeHandler(e.currentTarget.value)}
                        error={isSubmitted && postalCodeError}
                        data-test-selector="taxExemption_postalCode"
                    />
                </GridItem>
                <GridItem {...styles.phoneNumberGridItem}>
                    <TextField
                        name="phoneNumber"
                        label={translate("Phone Number")}
                        value={phoneNumber}
                        required
                        onChange={e => phoneNumberChangeHandler(e.currentTarget.value)}
                        onBlur={e => phoneNumberBlurHandler(e.currentTarget.value)}
                        error={isSubmitted && phoneNumberError}
                        data-test-selector="taxExemption_phoneNumber"
                    />
                </GridItem>
                <GridItem {...styles.emailAddressGridItem}>
                    <TextField
                        name="emailAddress"
                        label={translate("Email Address")}
                        value={emailAddress}
                        required
                        onChange={e => emailAddressChangeHandler(e.currentTarget.value)}
                        error={isSubmitted && emailAddressError}
                        data-test-selector="taxExemption_emailAddress"
                    />
                </GridItem>
                <GridItem {...styles.feinNumberGridItem}>
                    <TextField
                        type="feinNumber"
                        label={translate("FEIN Number")}
                        onChange={e => feinNumberChangeHandler(e.currentTarget.value)}
                        value={feinNumber}
                        data-test-selector="taxExemption_feinNumber"
                        required
                        error={isSubmitted && feinNumberError}
                    />
                </GridItem>
                <GridItem {...styles.stateRegistrationNumberGridItem}>
                    <TextField
                        type="stateRegistrationNumber"
                        label={translate("State Registration Number")}
                        onChange={e => stateRegistrationNumberChangeHandler(e.currentTarget.value)}
                        value={stateRegistrationNumber}
                        data-test-selector="taxExemption_stateRegistrationNumber"
                        required
                        error={isSubmitted && stateRegistrationNumberError}
                    />
                </GridItem>
                <GridItem {...styles.fileGridItem}>
                    <GridContainer {...styles.fileUploadWrapperGridContainer}>
                        {uploadComponents.map((item, i) => (
                            <GridItem
                                key={`fileUploadGridItem_${item.key}`}
                                {...styles.fileUploadWrapperGridItem}
                                data-test-selector="fileUploadWrapperGridItem"
                            >
                                <GridContainer
                                    {...styles.fileUploadGridContainer}
                                    data-test-selector="fileUploadGridContainer"
                                >
                                    <GridItem {...styles.fileUploadGridItem} data-test-selector="fileUploadGridItem">
                                        <FileUpload
                                            error={isSubmitted && pdfError}
                                            width="100%"
                                            uid={item.key}
                                            key={item.key}
                                            fileName={item.fileName}
                                            accept=".pdf"
                                            onFileChange={event =>
                                                handleFileUploadFileChangeEvent(event, item.key, event.target.files[0])
                                            }
                                            label={i === 0 ? "File must be a PDF" : ""}
                                            required
                                            buttonProps={{
                                                css: css`
                                                    color: black;
                                                `,
                                            }}
                                            placeholder="No file chosen"
                                        />
                                    </GridItem>
                                    <GridItem {...styles.removeFileUploadGridItem}>
                                        {i === 0 && (
                                            <Button
                                                onClick={event => removeUploadComponent(item.key)}
                                                {...styles.firstRemoveUploadButtonStyle}
                                            >
                                                Remove
                                            </Button>
                                        )}
                                        {i > 0 && (
                                            <Button
                                                onClick={event => removeUploadComponent(item.key)}
                                                {...styles.removeUploadButtonStyle}
                                            >
                                                Remove
                                            </Button>
                                        )}
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        ))}
                        <GridItem {...styles.addUploadComponentGridItem}>
                            <Button onClick={addUploadComponent} {...styles.addUploadButtonStyle}>
                                Upload another document
                            </Button>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.recaptchaGridItem}>
                    <SupplyLandAuthenticatedReCaptcha recaptchaRef={recaptchaRef} location="TaxExempt" />
                </GridItem>
                <GridItem {...styles.buttonGridItem}>
                    <Button
                        {...styles.submitButton}
                        onClick={uploadTaxExemptionFormHandler}
                        variant="primary"
                        disabled={isSubmitting || (isSubmitted && !isSubmitEnabled)}
                    >
                        Submit Tax Exemption Form
                    </Button>
                </GridItem>
            </GridContainer>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UploadTaxExemptionForm),
    definition: {
        group: "Testing",
        icon: "Banner",
    },
};

export default widgetModule;
